import { placeSections, RESULT_LAYOUT_COLUMNS } from './result-layout.ts';
import { buildReadModelTabs } from './tool-app-composition.ts';
import type {
  FlowComposerDraft,
  FlowComposerPreviewSample,
} from './types/flow-composer.ts';
import type {
  ResultComposition,
  ResultSectionSpec,
  ResultTabScope,
} from './types/result-composition.ts';
import { roleForViewType } from './types/result-composition.ts';
import type { DashboardComposition } from './types/dashboard-composition.ts';
import { createViewDefinitionForViewType } from './types/view-registry.ts';
import {
  DEFAULT_VIEW_SCHEMA_VERSION,
  type AnyViewEnvelope,
  type DataTableColumn,
  type DataTableRow,
  type FlowViewType,
  type ScoreCardFact,
} from './types/views.ts';

/**
 * Builds Forge preview envelopes / compositions by ARRANGING the LLM-authored
 * `previewSample` into canonical views. Never fabricates business values (see
 * docs/CONVENTIONS.md). Missing sample ⇒ honest `fallback_panel`, never a mock.
 */

type DraftMetrics = FlowComposerDraft['outputContract']['metrics'];

interface SectionContext {
  metrics: DraftMetrics;
  title: string;
  outputDescription: string;
  primaryScoreKey: string;
}

const cleanString = (value: string | undefined | null): string =>
  typeof value === 'string' ? value.trim() : '';

const sampleHasContent = (
  sample: FlowComposerPreviewSample | undefined,
): sample is FlowComposerPreviewSample => {
  if (!sample) return false;
  const hasEntities = sample.entities.some((entity) => cleanString(entity).length > 0);
  const hasMetrics = sample.metricSamples.some((metric) =>
    metric.values.some((value) => cleanString(value).length > 0));
  return hasEntities || hasMetrics || cleanString(sample.narrative).length > 0;
};

const metricValueAt = (
  sample: FlowComposerPreviewSample,
  key: string,
  index: number,
): string => {
  const entry = sample.metricSamples.find((metric) => metric.key === key);
  if (!entry) return '';
  return cleanString(entry.values[index]);
};

/** Parses a numeric score from an LLM-authored string. Returns null when absent. */
const parseScore = (value: string): number | null => {
  const match = value.match(/-?\d+(?:[.,]\d+)?/);
  if (!match) return null;
  const parsed = Number.parseFloat(match[0].replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const fallbackEnvelope = (title: string): AnyViewEnvelope => ({
  view_type: 'fallback_panel',
  schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
  data: {
    title: title || 'Aperçu',
    message:
      "Aucun exemple n'a encore été généré. Lance un test pour voir le résultat réel du tool.",
    reason: 'source_no_data',
  },
});

const primaryScoreKeyFor = (draft: FlowComposerDraft): string => {
  const metrics = draft.outputContract.metrics;
  return (
    draft.outputContract.defaults?.pulse_primary_score_key
    ?? metrics.find((metric) => metric.type === 'score')?.key
    ?? metrics[0]?.key
    ?? ''
  );
};

const buildSectionEnvelope = (
  viewType: FlowViewType,
  sample: FlowComposerPreviewSample | undefined,
  ctx: SectionContext,
): AnyViewEnvelope => {
  if (!sampleHasContent(sample)) return fallbackEnvelope(ctx.title);

  // A section owns its sample fields. Global metrics only supply labels for
  // matching keys; importing the entire contract creates unrelated empty columns.
  const metrics = sample.metricSamples.map((entry) => ({
    key: entry.key,
    label: ctx.metrics.find((metric) => metric.key === entry.key)?.label
      ?? entry.key.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2'),
  }));
  const entities = sample.entities.map(cleanString).filter((entity) => entity.length > 0);
  const narrative = cleanString(sample.narrative);
  const primaryScoreKey = ctx.primaryScoreKey;

  switch (viewType) {
    case 'data_table': {
      const columns: DataTableColumn[] = [
        { key: '__entity', label: 'Entité' },
        ...metrics.map((metric) => ({ key: metric.key, label: metric.label })),
      ];
      const rows: DataTableRow[] = (entities.length > 0 ? entities : ['—']).map((entity, index) => ({
        id: `preview-${index}`,
        cells: [
          { key: '__entity', value: entity },
          ...metrics.map((metric) => ({ key: metric.key, value: metricValueAt(sample, metric.key, index) || null })),
        ],
      }));
      return {
        view_type: 'data_table',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: { title: ctx.outputDescription || ctx.title, columns, rows },
      };
    }

    case 'chart': {
      // Example only: one bar per entity (or per metric when there is no
      // entity), one series per numeric metric of the sample.
      const categories = entities.length > 0 ? entities : metrics.map((metric) => metric.label);
      const series = (entities.length > 0 ? metrics : metrics.slice(0, 1)).map((metric) => ({
        key: metric.key,
        label: entities.length > 0 ? metric.label : (ctx.outputDescription || ctx.title),
        values: categories.map((_, index) => parseScore(entities.length > 0
          ? metricValueAt(sample, metric.key, index)
          : metricValueAt(sample, metrics[index]?.key ?? '', 0))),
      }));
      return {
        view_type: 'chart',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: ctx.outputDescription || ctx.title,
          kind: 'bar',
          categories,
          series,
          ...(narrative ? { insight: narrative } : {}),
        },
      };
    }

    case 'timeline': {
      // The sample carries no dates and a preview never invents one: the
      // timeline shows its title and says what will fill it.
      return {
        view_type: 'timeline',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: ctx.outputDescription || ctx.title,
          events: [],
          empty_message: narrative || 'Les événements datés apparaîtront ici à chaque exécution.',
        },
      };
    }

    case 'metric_grid': {
      return {
        view_type: 'metric_grid',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: ctx.outputDescription || ctx.title,
          metrics: metrics.map((metric) => ({
            label: metric.label,
            value: metricValueAt(sample, metric.key, 0) || '—',
          })),
        },
      };
    }

    case 'score_card': {
      const rawScore = metricValueAt(sample, primaryScoreKey, 0);
      const score = parseScore(rawScore);
      const facts: ScoreCardFact[] = metrics
        .filter((metric) => metric.key !== primaryScoreKey)
        .map((metric) => ({ label: metric.label, value: metricValueAt(sample, metric.key, 0) || '—' }));
      return {
        view_type: 'score_card',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: ctx.title,
          ...(score !== null ? { score, score_label: rawScore } : {}),
          status: 'neutral',
          ...(narrative ? { insight: narrative } : {}),
          facts,
        },
      };
    }

    case 'health_gauge': {
      const overall = parseScore(metricValueAt(sample, primaryScoreKey, 0));
      return {
        view_type: 'health_gauge',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          entity_name: entities[0] || ctx.title,
          entity_type: 'account',
          overall_score: overall ?? 0,
          overall_status: 'healthy',
          last_updated: 'Aperçu',
          ...(narrative ? { recommended_action: narrative } : {}),
          dimensions: metrics.map((metric) => ({
            label: metric.label,
            score: parseScore(metricValueAt(sample, metric.key, 0)) ?? 0,
            weight: 1,
            signals: [],
          })),
        },
      };
    }

    case 'entity_summary': {
      return {
        view_type: 'entity_summary',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          entity: { name: entities[0] || ctx.title, type: 'account' },
          headline: narrative || ctx.outputDescription,
          key_facts: metrics.map((metric) => ({
            label: metric.label,
            value: metricValueAt(sample, metric.key, 0) || '—',
          })),
        },
      };
    }

    case 'action_list': {
      const labels = entities.length > 0 ? entities : metrics.map((metric) => metric.label);
      return {
        view_type: 'action_list',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: ctx.title,
          ...(narrative ? { description: narrative } : {}),
          items: labels.map((label, index) => ({
            id: `preview-${index}`,
            label,
            priority: 'medium',
            status: 'pending',
          })),
        },
      };
    }

    case 'artifact_document': {
      return {
        view_type: 'artifact_document',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: ctx.title,
          subject: entities[0] || ctx.title,
          body_format: 'markdown',
          body: narrative || ctx.outputDescription,
          meta_items: metrics.map((metric) => ({
            label: metric.label,
            value: metricValueAt(sample, metric.key, 0) || '—',
          })),
          export_actions: ['copy_markdown'],
        },
      };
    }
  }
};

/** Single-view preview envelope (back-compat for single-section tools). */
export const buildComposerPreviewEnvelope = (draft: FlowComposerDraft): AnyViewEnvelope =>
  buildSectionEnvelope(draft.viewType, draft.previewSample, {
    metrics: draft.outputContract.metrics,
    title: draft.title,
    outputDescription: draft.outputDescription,
    primaryScoreKey: primaryScoreKeyFor(draft),
  });

export interface ComposerPreviewTab {
  id: string;
  label: string;
  scope: ResultTabScope;
  composition: DashboardComposition;
  viewEnvelopesByInstanceId: Record<string, AnyViewEnvelope>;
}

/** Derives the result tabs (single tab when no composite spec) for the Forge preview. */
const resolvePreviewSections = (draft: FlowComposerDraft): Array<{
  tab: { id: string; label: string; scope: ResultTabScope };
  sections: ResultSectionSpec[];
}> => {
  const composition = draft.resultComposition;
  if (composition && composition.tabs.length > 0) {
    return composition.tabs.map((tab) => ({
      tab: { id: tab.id, label: tab.label, scope: tab.scope },
      sections: tab.sections,
    }));
  }
  return [{
    tab: { id: 'result', label: 'Résultat', scope: 'run' },
    sections: [{
      id: 'result-main',
      role: roleForViewType(draft.viewType),
      viewType: draft.viewType,
      label: draft.outputDescription || draft.title,
      previewSample: draft.previewSample,
    }],
  }];
};

/**
 * Builds the multi-tab, multi-section composition for the Forge preview, rendered
 * by `ToolResultPageRenderer` exactly like the real tool result page.
 */
export const buildComposerPreviewComposition = (draft: FlowComposerDraft): ComposerPreviewTab[] => {
  if (draft.readModel) return buildReadModelTabs(draft.readModel);
  const ctxBase = {
    metrics: draft.outputContract.metrics,
    title: draft.title,
    outputDescription: draft.outputDescription,
    primaryScoreKey: primaryScoreKeyFor(draft),
  };

  return resolvePreviewSections(draft).map(({ tab, sections }) => {
    const viewEnvelopesByInstanceId: Record<string, AnyViewEnvelope> = {};
    const placements = placeSections(sections.map((section) => section.width));
    const views = sections.map((section, index) => {
      const instanceId = `${tab.id}-${section.id}`;
      viewEnvelopesByInstanceId[instanceId] = buildSectionEnvelope(section.viewType, section.previewSample, { ...ctxBase, title: section.label || ctxBase.title, outputDescription: section.label || ctxBase.outputDescription });
      return {
        id: instanceId,
        viewDefinition: createViewDefinitionForViewType(section.viewType, {
          title: section.label || ctxBase.title,
        }),
        placement: { position: placements[index]! },
        previewEnvelope: viewEnvelopesByInstanceId[instanceId],
        provenance: { type: 'generated' as const, capturedAt: new Date(0).toISOString() },
      };
    });

    const composition: DashboardComposition = {
      version: '1.0',
      metadata: { title: tab.label, visibility: 'private' },
      layout: { type: 'grid', columns: RESULT_LAYOUT_COLUMNS },
      views,
    };

    return { id: tab.id, label: tab.label, scope: tab.scope, composition, viewEnvelopesByInstanceId };
  });
};

/**
 * Always return a concrete, editable ResultComposition for a draft — even when
 * the draft has none yet (the single default tab/section is materialized). The
 * Forge UX toolbar edits this (section/tab labels, section order) and persists
 * it back as a `ui_edit`, with no domino/data change and therefore no rerun.
 */
export const materializeResultComposition = (
  draft: FlowComposerDraft,
): ResultComposition => ({
  tabs: resolvePreviewSections(draft).map(({ tab, sections }) => ({
    id: tab.id,
    label: tab.label,
    scope: tab.scope,
    sections,
  })),
});
